/**
 * Admin Metrics API Service
 *
 * Centralized API client for operation metrics and pricing calibration.
 * Uses the centralized URL resolver and HTTP utilities.
 *
 * Aligned with backend routes at serverless/api/routes/admin/operation_metrics.py
 */

import { buildCloudApiUrl } from './urlResolver';
import { handleResponse, getAuthHeaders } from '../utils/httpUtils';

/**
 * Endpoints for admin metrics
 * Aligned with backend router at /api/v1/admin/metrics
 */
const ENDPOINTS = {
  report: '/api/v1/admin/metrics/report',
  operationReport: (operation: string) => `/api/v1/admin/metrics/report/${operation}`,
  timeByParams: (operation: string) => `/api/v1/admin/metrics/time-by-params/${operation}`,
  pricingRecommendations: '/api/v1/admin/metrics/pricing-recommendations',
  stats: (operation: string) => `/api/v1/admin/metrics/stats/${operation}`,
  operations: '/api/v1/admin/metrics/operations',
  operationsList: '/api/v1/admin/metrics/operations/list',
  users: '/api/v1/admin/metrics/users',
  countries: '/api/v1/admin/metrics/countries',
  categories: '/api/v1/admin/metrics/categories',
  usageTrends: '/api/v1/admin/metrics/usage-trends',
} as const;

/**
 * Statistics for a single operation type
 */
export interface OperationTimeStats {
  operation: string;
  provider: string | null;
  sample_count: number;
  success_count: number;
  failure_count: number;
  avg_processing_seconds: number;
  min_processing_seconds: number;
  max_processing_seconds: number;
  p50_processing_seconds: number;
  p90_processing_seconds: number;
  p99_processing_seconds: number;
  avg_tokens_charged: number;
  avg_tokens_per_second: number;
  tokens_vs_seconds_ratio: number;
  pricing_status: 'ok' | 'undercharging' | 'overcharging' | 'low_confidence' | 'insufficient_data';
}

/**
 * Pricing recommendation based on measured data
 */
export interface PricingRecommendation {
  operation: string;
  provider: string | null;
  current_estimate: string;
  measured_avg_seconds: number;
  current_avg_tokens: number;
  recommended_tokens: number;
  variance_percent: number;
  urgency: 'low' | 'medium' | 'high' | 'critical';
  sample_count: number;
  confidence: 'low' | 'medium' | 'high';
}

/**
 * Complete metrics report response
 */
export interface MetricsReport {
  period_days: number;
  start_date: string;
  end_date: string;
  total_operations: number;
  total_processing_seconds: number;
  total_tokens_charged: number;
  overall_tokens_per_second: number;
  operation_stats: OperationTimeStats[];
  recommendations: PricingRecommendation[];
}

/**
 * Unified metrics filters for all endpoints
 */
export interface MetricsFilters {
  hours?: number;
  days?: number;
  start_date?: string;
  end_date?: string;
  operation?: string;
  operations?: string[];
  category?: string;
  user_id?: string;
  country_code?: string;
  environment?: string;
}

/**
 * Individual operation record
 */
export interface IndividualOperation {
  id: string;
  job_id: string;
  user_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
  country_code: string | null;
  operation: string;
  provider: string;
  processing_seconds: number;
  tokens_charged: number;
  tokens_per_second: number;
  input_params: Record<string, unknown>;
  success: boolean;
  error_message: string | null;
  environment: string;
  created_at: string;
}

/**
 * Paginated response for individual operations
 */
export interface PaginatedOperations {
  items: IndividualOperation[];
  next_cursor: string | null;
  total_in_page: number;
}

/**
 * User breakdown entry
 */
export interface UserBreakdown {
  user_id: string;
  user_email: string | null;
  user_display_name: string | null;
  total_operations: number;
  total_tokens: number;
  total_seconds: number;
  success_count: number;
  failure_count: number;
  last_active: string | null;
}

/**
 * Country breakdown entry
 */
export interface CountryBreakdown {
  country_code: string;
  total_operations: number;
  total_tokens: number;
  total_seconds: number;
  unique_users: number;
}

/**
 * Operation categories mapping
 */
export type OperationCategories = Record<string, string[]>;

/**
 * Processing time grouped by parameter
 */
export interface OperationTimeByParams {
  operation: string;
  param_name: string;
  param_value: string | number;
  sample_count: number;
  avg_processing_seconds: number;
  avg_tokens_charged: number;
  recommended_tokens: number;
}

/**
 * Detailed report for a single operation
 */
export interface OperationReport {
  operation: string;
  period_days: number;
  start_date: string;
  end_date: string;
  stats: OperationTimeStats;
  by_provider: OperationTimeStats[];
  by_params: OperationTimeByParams[];
  recommendations: PricingRecommendation[];
}

/**
 * Get comprehensive metrics report
 *
 * Returns processing time statistics for all operations with pricing recommendations.
 * Requires admin authentication.
 *
 * @param days - Number of days to analyze (default: 7, max: 90)
 * @param token - Firebase ID token with admin claim
 * @param environment - Optional environment filter (production, staging, local)
 */
export async function fetchMetricsReport(
  days: number = 7,
  token: string,
  environment?: string
): Promise<MetricsReport> {
  let url = `${buildCloudApiUrl(ENDPOINTS.report)}?days=${days}`;
  if (environment) {
    url += `&environment=${environment}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  return handleResponse<MetricsReport>(response);
}

/**
 * Get pricing recommendations based on measured data
 *
 * Returns recommendations for operations where actual processing time
 * significantly differs from current token estimates.
 * Requires admin authentication.
 *
 * @param days - Number of days to analyze
 * @param token - Firebase ID token with admin claim
 * @param urgency - Optional urgency filter (low, medium, high, critical)
 */
export async function fetchPricingRecommendations(
  days: number = 7,
  token: string,
  urgency?: string
): Promise<PricingRecommendation[]> {
  let url = `${buildCloudApiUrl(ENDPOINTS.pricingRecommendations)}?days=${days}`;
  if (urgency) {
    url += `&urgency=${urgency}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  const data = await handleResponse<{
    period_days: number;
    total_recommendations: number;
    recommendations: PricingRecommendation[];
  }>(response);

  return data.recommendations;
}

/**
 * Get simple statistics for an operation
 *
 * Lighter weight than full report, returns basic stats.
 * Requires admin authentication.
 *
 * @param operation - Operation type
 * @param days - Number of days to analyze
 * @param token - Firebase ID token with admin claim
 * @param provider - Optional provider filter
 */
export async function fetchOperationStats(
  operation: string,
  days: number = 7,
  token: string,
  provider?: string
): Promise<OperationTimeStats> {
  let url = `${buildCloudApiUrl(ENDPOINTS.stats(operation))}?days=${days}`;
  if (provider) {
    url += `&provider=${provider}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  return handleResponse<OperationTimeStats>(response);
}

/**
 * Get detailed report for a specific operation
 *
 * Includes time-by-params breakdowns and provider comparisons.
 * Requires admin authentication.
 *
 * @param operation - Operation type
 * @param days - Number of days to analyze
 * @param token - Firebase ID token with admin claim
 * @param environment - Optional environment filter (production, staging, local)
 */
export async function fetchOperationReport(
  operation: string,
  days: number = 7,
  token: string,
  environment?: string
): Promise<OperationReport> {
  let url = `${buildCloudApiUrl(ENDPOINTS.operationReport(operation))}?days=${days}`;
  if (environment) {
    url += `&environment=${environment}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  return handleResponse<OperationReport>(response);
}

/**
 * List all supported operation types
 *
 * Requires admin authentication.
 *
 * @param token - Firebase ID token with admin claim
 */
export async function fetchSupportedOperations(token: string): Promise<string[]> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.operations), {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  const data = await handleResponse<{ operations: string[] }>(response);
  return data.operations;
}

/**
 * Build filter params for API requests
 */
function buildFilterParams(filters: MetricsFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.hours) params.set('hours', String(filters.hours));
  if (filters.days) params.set('days', String(filters.days));
  if (filters.start_date) params.set('start_date', filters.start_date);
  if (filters.end_date) params.set('end_date', filters.end_date);
  if (filters.operation) params.set('operation', filters.operation);
  if (filters.operations?.length) params.set('operations', filters.operations.join(','));
  if (filters.category) params.set('category', filters.category);
  if (filters.user_id) params.set('user_id', filters.user_id);
  if (filters.country_code) params.set('country_code', filters.country_code);
  if (filters.environment) params.set('environment', filters.environment);
  return params;
}

/**
 * Fetch individual operations with pagination
 *
 * Returns paginated list of individual operation records.
 * Requires admin authentication.
 *
 * @param filters - Metrics filters
 * @param token - Firebase ID token with admin claim
 * @param cursor - Pagination cursor (optional)
 * @param limit - Number of records per page (default: 50)
 */
export async function fetchIndividualOperations(
  filters: MetricsFilters,
  token: string,
  cursor?: string,
  limit: number = 50
): Promise<PaginatedOperations> {
  const params = buildFilterParams(filters);
  if (cursor) params.set('cursor', cursor);
  params.set('limit', String(limit));

  const url = `${buildCloudApiUrl(ENDPOINTS.operationsList)}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  return handleResponse<PaginatedOperations>(response);
}

/**
 * Fetch user breakdown
 *
 * Returns aggregated statistics by user.
 * Requires admin authentication.
 *
 * @param filters - Metrics filters
 * @param token - Firebase ID token with admin claim
 */
export async function fetchUserBreakdown(
  filters: MetricsFilters,
  token: string
): Promise<UserBreakdown[]> {
  const params = buildFilterParams(filters);
  const url = `${buildCloudApiUrl(ENDPOINTS.users)}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  const data = await handleResponse<{ users: UserBreakdown[]; total: number }>(response);
  return data.users;
}

/**
 * Fetch country breakdown
 *
 * Returns aggregated statistics by country.
 * Requires admin authentication.
 *
 * @param filters - Metrics filters
 * @param token - Firebase ID token with admin claim
 */
export async function fetchCountryBreakdown(
  filters: MetricsFilters,
  token: string
): Promise<CountryBreakdown[]> {
  const params = buildFilterParams(filters);
  const url = `${buildCloudApiUrl(ENDPOINTS.countries)}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  const data = await handleResponse<{ countries: CountryBreakdown[]; total: number }>(response);
  return data.countries;
}

/**
 * Fetch operation categories
 *
 * Returns mapping of category names to operation lists.
 * Requires admin authentication.
 *
 * @param token - Firebase ID token with admin claim
 */
export async function fetchCategories(token: string): Promise<OperationCategories> {
  const url = buildCloudApiUrl(ENDPOINTS.categories);
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });
  const data = await handleResponse<{ categories: OperationCategories }>(response);
  return data.categories;
}

/**
 * A single time-bucketed usage data point for trend charts.
 */
export interface UsageTrendBucket {
  date: string;
  total_operations: number;
  total_tokens: number;
  total_seconds: number;
  success_count: number;
  failure_count: number;
  unique_users: number;
}

/**
 * Fetch bucketed usage trend data for chart visualization.
 *
 * Returns daily/hourly/weekly aggregated metrics.
 * Requires admin authentication.
 *
 * @param filters - Metrics filters (days/hours/start_date/end_date/environment/operation)
 * @param token - Firebase ID token with admin claim
 * @param bucket - Bucket size: 'hour' | 'day' | 'week' (default 'day')
 */
export async function fetchUsageTrends(
  filters: MetricsFilters,
  token: string,
  bucket: 'hour' | 'day' | 'week' = 'day'
): Promise<UsageTrendBucket[]> {
  const params = buildFilterParams(filters);
  params.set('bucket', bucket);

  const url = `${buildCloudApiUrl(ENDPOINTS.usageTrends)}?${params.toString()}`;
  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(token),
  });

  const data = await handleResponse<{
    buckets: UsageTrendBucket[];
    total_buckets: number;
    bucket_size: string;
  }>(response);

  return data.buckets;
}

/**
 * Grouped export for convenient importing
 */
export const metricsApi = {
  fetchMetricsReport,
  fetchPricingRecommendations,
  fetchOperationStats,
  fetchOperationReport,
  fetchSupportedOperations,
  fetchIndividualOperations,
  fetchUserBreakdown,
  fetchCountryBreakdown,
  fetchCategories,
  fetchUsageTrends,
};
